import { HttpClient } from '@angular/common/http';
import { Observable, from, switchMap } from 'rxjs';

export function withSecureHeaders<T>(
  http: HttpClient,
  baseUrl: string,
  request: (headers: Record<string, string>) => Observable<T>,
): Observable<T> {
  return http
    .get(baseUrl + '/users/user-info', { responseType: 'text' })
    .pipe(switchMap((secret) => from(generateRequestHeaders(secret)).pipe(switchMap(request))));
}

// Utility function to convert string to Uint8Array
const str2ab = (str: string) => {
  const buf = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i++) {
    buf[i] = str.charCodeAt(i);
  }
  return buf;
};

const buf2hex = (buffer: ArrayBuffer | ArrayBufferView) => {
  // Normalize to a Uint8Array covering the exact bytes of the input
  const bytes =
    buffer instanceof ArrayBuffer
      ? new Uint8Array(buffer)
      : new Uint8Array(
          (buffer as ArrayBufferView).buffer,
          (buffer as ArrayBufferView).byteOffset,
          (buffer as ArrayBufferView).byteLength,
        );

  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

// Generate random bytes for nonce
const generateNonce = () => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return buf2hex(array);
};

// Generate HMAC signature using Web Crypto API
const generateSignature = async (message: string, secretKey: string) => {
  const encoder = new TextEncoder();
  const keyData = encoder.encode(secretKey);

  // Import the secret key
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    keyData,
    {
      name: 'HMAC',
      hash: { name: 'SHA-256' },
    },
    false,
    ['sign'],
  );

  // Sign the message
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message));

  return buf2hex(signature);
};

// Main function to generate headers
export async function generateRequestHeaders(secretKey: string) {
  const nonce = generateNonce();
  const timestamp = Date.now().toString();

  // Generate signature from nonce and timestamp
  const signature = await generateSignature(`${nonce}:${timestamp}`, secretKey);

  return {
    'x-nonce': nonce,
    'x-timestamp': timestamp,
    'x-signature': signature,
  };
}
